import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { CheckCircle2, MinusCircle, OctagonMinus, XCircle } from "lucide-react";
import { GovernanceDataTable } from "@/components/governance/GovernanceDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useGovernance } from "@/hooks/useGovernance";
import { VoteChoice } from "@/types/governance";

interface VotingRow {
  id: string;
  title: string;
  committeeName: string;
  meetingTitle: string;
  status: string;
  deadline: string;
  yes: number;
  no: number;
  abstain: number;
}

const currentUserId = "m-2";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" }) : "-";

const getVoteCounts = (votes: { vote: VoteChoice }[]) =>
  votes.reduce(
    (acc, item) => {
      acc[item.vote] += 1;
      return acc;
    },
    { yes: 0, no: 0, abstain: 0 },
  );

export default function VotingDecisions() {
  const {
    getActiveFramework,
    getVotingDecisionsByFramework,
    meetings,
    committees,
    members,
    getMeetingById,
    getCommitteeById,
    getMemberById,
    castVote,
    updateVotingDecisionStatus,
  } = useGovernance();
  const activeFramework = getActiveFramework();
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const decisions = useMemo(
    () => (activeFramework ? getVotingDecisionsByFramework(activeFramework.id) : []),
    [activeFramework, getVotingDecisionsByFramework],
  );

  const rows = useMemo(
    () =>
      decisions.map((decision) => {
        const counts = getVoteCounts(decision.votes);
        return {
          id: decision.id,
          title: decision.title,
          committeeName: getCommitteeById(decision.committeeId)?.name ?? "Unknown Committee",
          meetingTitle: getMeetingById(decision.meetingId)?.title ?? "Unknown Meeting",
          status: decision.status,
          deadline: decision.deadline ?? "",
          yes: counts.yes,
          no: counts.no,
          abstain: counts.abstain,
        } satisfies VotingRow;
      }),
    [decisions, getCommitteeById, getMeetingById],
  );

  const selectedDecision = useMemo(
    () => decisions.find((item) => item.id === selectedDecisionId),
    [decisions, selectedDecisionId],
  );
  const selectedMeeting = selectedDecision ? getMeetingById(selectedDecision.meetingId) : undefined;
  const selectedCommittee = selectedDecision ? getCommitteeById(selectedDecision.committeeId) : undefined;
  const committeeMembers = useMemo(
    () =>
      selectedCommittee
        ? members.filter((member) => selectedCommittee.memberIds.includes(member.id))
        : [],
    [members, selectedCommittee],
  );

  const stats = useMemo(
    () => ({
      open: decisions.filter((item) => item.status === "open").length,
      passed: decisions.filter((item) => item.status === "passed").length,
      rejected: decisions.filter((item) => item.status === "rejected").length,
      total: decisions.length,
    }),
    [decisions],
  );

  const columns: ColumnDef<VotingRow>[] = [
    {
      accessorKey: "title",
      header: "Decision Title",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.title}</div>
          <div className="text-xs text-muted-foreground">{row.original.committeeName}</div>
        </div>
      ),
    },
    {
      accessorKey: "meetingTitle",
      header: "Source Meeting",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.status === "open" ? "outline" : "secondary"}>{row.original.status}</Badge>
      ),
    },
    {
      accessorKey: "yes",
      header: "Vote Breakdown",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-xs text-muted-foreground">
          Yes {row.original.yes} • No {row.original.no} • Abstain {row.original.abstain}
        </div>
      ),
    },
    {
      accessorKey: "deadline",
      header: "Deadline",
      cell: ({ row }) => formatDate(row.original.deadline),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedDecisionId(row.original.id);
              setSheetOpen(true);
            }}
          >
            View Vote
          </Button>
        </div>
      ),
    },
  ];

  if (!activeFramework) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-medium text-foreground">Voting & Decisions</h1>
          <p className="text-muted-foreground">Create your governance framework before tracking votes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-medium text-foreground">Voting & Decisions</h1>
        <p className="text-muted-foreground">
          Decision outcomes are derived from committee meeting notes and tracked centrally.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-card">
          <CardContent className="py-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Open Votes</p>
            <p className="mt-2 text-3xl font-semibold">{stats.open}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="py-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Passed</p>
            <p className="mt-2 text-3xl font-semibold">{stats.passed}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="py-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Rejected</p>
            <p className="mt-2 text-3xl font-semibold">{stats.rejected}</p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="py-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Decisions</p>
            <p className="mt-2 text-3xl font-semibold">{stats.total}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Decision Register</CardTitle>
        </CardHeader>
        <CardContent>
          <GovernanceDataTable columns={columns} data={rows} emptyMessage="No voting decisions available." />
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{selectedDecision?.title ?? "Voting Decision"}</SheetTitle>
            <SheetDescription>{selectedDecision?.description ?? "Decision details"}</SheetDescription>
          </SheetHeader>

          {selectedDecision && selectedMeeting && selectedCommittee ? (
            <div className="mt-6 space-y-6">
              <div className="rounded-md border p-3 text-sm">
                <p className="font-medium">Source Meeting</p>
                <p className="mt-1 text-muted-foreground">{selectedMeeting.title}</p>
                <Link to="/governance/meetings" className="mt-2 inline-block text-xs text-primary">
                  Open Meeting Minutes
                </Link>
              </div>

              <div className="rounded-md border p-3 text-sm">
                <p className="font-medium">Committee & Referenced ToR</p>
                <p className="mt-1 text-muted-foreground">{selectedCommittee.name}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedMeeting.referencedTorIds.length ? (
                    selectedMeeting.referencedTorIds.map((torId) => (
                      <Badge key={torId} variant="secondary">
                        {torId}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="outline">No ToR referenced</Badge>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Voting Panel</p>
                {committeeMembers.map((member) => {
                  const vote = selectedDecision.votes.find((item) => item.memberId === member.id);
                  return (
                    <div key={member.id} className="flex items-center justify-between rounded-md border p-3 text-sm">
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.designation}</p>
                      </div>
                      <Badge variant={vote ? "secondary" : "outline"}>{vote?.vote ?? "not cast"}</Badge>
                    </div>
                  );
                })}
              </div>

              {selectedDecision.status === "open" ? (
                <div className="space-y-3 rounded-md border p-3">
                  <p className="text-sm font-medium">Cast Your Vote</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => castVote(selectedDecision.id, currentUserId, "yes")}>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Vote Yes
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => castVote(selectedDecision.id, currentUserId, "no")}>
                      <XCircle className="mr-2 h-4 w-4" />
                      Vote No
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => castVote(selectedDecision.id, currentUserId, "abstain")}
                    >
                      <MinusCircle className="mr-2 h-4 w-4" />
                      Abstain
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="space-y-2 rounded-md border p-3">
                <p className="text-sm font-medium">Chair Decision Controls</p>
                <p className="text-xs text-muted-foreground">
                  Status can be finalized by the committee chair after vote review.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateVotingDecisionStatus(selectedDecision.id, "passed")}
                    disabled={selectedCommittee.chairpersonId !== currentUserId}
                  >
                    Mark Passed
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateVotingDecisionStatus(selectedDecision.id, "rejected")}
                    disabled={selectedCommittee.chairpersonId !== currentUserId}
                  >
                    Mark Rejected
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateVotingDecisionStatus(selectedDecision.id, "withdrawn")}
                    disabled={selectedCommittee.chairpersonId !== currentUserId}
                  >
                    <OctagonMinus className="mr-2 h-4 w-4" />
                    Withdraw
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateVotingDecisionStatus(selectedDecision.id, "open")}
                    disabled={selectedCommittee.chairpersonId !== currentUserId}
                  >
                    Reopen
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <SheetFooter className="mt-6">
            <Badge variant="outline">Deadline: {formatDate(selectedDecision?.deadline)}</Badge>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
